import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../base/Button';
import Input from '../base/Input';
import { useNavigate } from 'react-router-dom';
import LocationAutocomplete from './LocationAutocomplete';

type SearchType = 'hotels' | 'flights' | 'cars' | 'packages' | 'activities' | 'hajj-umrah';

interface FlightSegment {
  id: string;
  from: string;
  to: string;
  date: string;
}

export default function SearchWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SearchType>('flights');
  const [flightType, setFlightType] = useState('roundtrip');
  const [flightClass, setFlightClass] = useState('economy');
  const [flightFrom, setFlightFrom] = useState('');
  const [flightTo, setFlightTo] = useState('');
  const [hotelDestination, setHotelDestination] = useState('');
  const [travelers, setTravelers] = useState({ adults: 2, children: 0, rooms: 1 });
  const [showTravelers, setShowTravelers] = useState(false);
  const [hajjPackageType, setHajjPackageType] = useState('hajj');
  const [customPackage, setCustomPackage] = useState({
    accommodation: 'standard',
    transportation: 'bus',
    meals: 'full-board',
    guidance: 'group',
    duration: '14'
  });

  // Multi-city flight segments
  const [flightSegments, setFlightSegments] = useState<FlightSegment[]>([
    { id: '1', from: '', to: '', date: '' },
    { id: '2', from: '', to: '', date: '' }
  ]);

  const tabs = [
    { id: 'flights', label: t('search.flights'), icon: 'ri-flight-takeoff-line' },
    { id: 'hotels', label: t('search.hotels'), icon: 'ri-hotel-line' },
    { id: 'taxi', label: t('search.taxi'), icon: 'ri-taxi-line' },
    { id: 'packages', label: t('search.packages'), icon: 'ri-suitcase-line' },
    { id: 'aiBooking', label: t('search.aiBooking'), icon: 'ri-robot-line' },
    { id: 'hajjUmrah', label: t('search.hajjUmrah'), icon: 'ri-building-line' },
  ];

  const addFlightSegment = () => {
    const newSegment: FlightSegment = {
      id: Date.now().toString(),
      from: '',
      to: '',
      date: ''
    };
    setFlightSegments([...flightSegments, newSegment]);
  };

  const removeFlightSegment = (id: string) => {
    if (flightSegments.length > 2) {
      setFlightSegments(flightSegments.filter(segment => segment.id !== id));
    }
  };

  const updateFlightSegment = (id: string, field: keyof FlightSegment, value: string) => {
    setFlightSegments(flightSegments.map(segment => 
      segment.id === id ? { ...segment, [field]: value } : segment
    ));
  };

  const handleSearch = () => {
    // In a real app we'd construct a detailed query string based on the activeTab and its state
    const params = new URLSearchParams();
    params.append('type', activeTab);
    
    if (activeTab === 'flights') {
      params.append('flightType', flightType);
      params.append('adults', travelers.adults.toString());
      if (flightFrom) params.append('origin', flightFrom);
      if (flightTo) params.append('destination', flightTo);
    } else if (activeTab === 'hotels') {
      if (hotelDestination) params.append('destination', hotelDestination);
    }
    
    navigate(`/search?${params.toString()}`);
  };

  const renderHotelsSearch = () => (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div className="md:col-span-2">
        <LocationAutocomplete
          value={hotelDestination}
          onChange={setHotelDestination}
          placeholder={t('search.destination')}
          icon="ri-map-pin-line"
          className="text-sm"
        />
      </div>
      <div>
        <Input
          type="date"
          placeholder={t('search.checkIn')}
          icon="ri-calendar-line"
          className="text-sm"
        />
      </div>
      <div>
        <Input
          type="date"
          placeholder={t('search.checkOut')}
          icon="ri-calendar-line"
          className="text-sm"
        />
      </div>
    </div>
  );

  const renderFlightsSearch = () => (
    <div className="space-y-4">
      <div className="flex space-x-4">
        <label className="flex items-center">
          <input
            type="radio"
            name="flightType"
            value="roundtrip"
            checked={flightType === 'roundtrip'}
            onChange={(e) => setFlightType(e.target.value)}
            className="mr-2"
          />
          {t('flights.roundTrip')}
        </label>
        <label className="flex items-center">
          <input
            type="radio"
            name="flightType"
            value="oneway"
            checked={flightType === 'oneway'}
            onChange={(e) => setFlightType(e.target.value)}
            className="mr-2"
          />
          {t('flights.oneWay')}
        </label>
        <label className="flex items-center">
          <input
            type="radio"
            name="flightType"
            value="multicity"
            checked={flightType === 'multicity'}
            onChange={(e) => setFlightType(e.target.value)}
            className="mr-2"
          />
          {t('flights.multiCity')}
        </label>
      </div>
      
      {flightType === 'multicity' ? (
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-3 flex items-center">
              <i className="ri-route-line mr-2"></i>
              Multi-City Flight Itinerary
            </h3>
            
            {flightSegments.map((segment, index) => (
              <div key={segment.id} className="bg-white p-4 rounded-lg mb-3 border border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">
                    Flight {index + 1}
                  </h4>
                  {flightSegments.length > 2 && (
                    <button
                      onClick={() => removeFlightSegment(segment.id)}
                      className="text-red-500 hover:text-red-700 cursor-pointer"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <LocationAutocomplete
                      value={segment.from}
                      onChange={(val) => updateFlightSegment(segment.id, 'from', val)}
                      placeholder={`From (Flight ${index + 1})`}
                      icon="ri-takeoff-line"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <LocationAutocomplete
                      value={segment.to}
                      onChange={(val) => updateFlightSegment(segment.id, 'to', val)}
                      placeholder={`To (Flight ${index + 1})`}
                      icon="ri-flight-land-line"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Input
                      type="date"
                      placeholder="Departure Date"
                      icon="ri-calendar-line"
                      value={segment.date}
                      onChange={(e) => updateFlightSegment(segment.id, 'date', e.target.value)}
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}
            
            <button
              onClick={addFlightSegment}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
            >
              <i className="ri-add-circle-line"></i>
              <span>Add Another Flight</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <LocationAutocomplete
              value={flightFrom}
              onChange={setFlightFrom}
              placeholder={t('search.from')}
              icon="ri-takeoff-line"
              className="text-sm"
            />
          </div>
          <div>
            <LocationAutocomplete
              value={flightTo}
              onChange={setFlightTo}
              placeholder={t('search.to')}
              icon="ri-flight-land-line"
              className="text-sm"
            />
          </div>
          <div>
            <Input
              type="date"
              placeholder={t('search.departure')}
              icon="ri-calendar-line"
              className="text-sm"
            />
          </div>
          {flightType === 'roundtrip' && (
            <div>
              <Input
                type="date"
                placeholder={t('search.return')}
                icon="ri-calendar-line"
                className="text-sm"
              />
            </div>
          )}
        </div>
      )}

      {/* Flight Class Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Flight Class
        </label>
        <select 
          value={flightClass}
          onChange={(e) => setFlightClass(e.target.value)}
          className="w-full md:w-64 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
        >
          <option value="economy">Economy</option>
          <option value="premium-economy">Premium Economy</option>
          <option value="business">Business Class</option>
          <option value="first">First Class</option>
        </select>
      </div>
    </div>
  );

  const renderCarsSearch = () => (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div>
        <Input
          placeholder={t('search.pickUp')}
          icon="ri-map-pin-line"
          className="text-sm"
        />
      </div>
      <div>
        <Input
          placeholder={t('search.dropOff')}
          icon="ri-map-pin-line"
          className="text-sm"
        />
      </div>
      <div>
        <Input
          type="date"
          placeholder={t('search.pickUpDate')}
          icon="ri-calendar-line"
          className="text-sm"
        />
      </div>
      <div>
        <Input
          type="time"
          placeholder={t('search.pickUpDate')}
          icon="ri-time-line"
          className="text-sm"
        />
      </div>
    </div>
  );

  const renderPackagesSearch = () => (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div className="md:col-span-2">
        <Input
          placeholder={t('search.destination')}
          icon="ri-map-pin-line"
          className="text-sm"
        />
      </div>
      <div>
        <Input
          type="date"
          placeholder={t('search.departure')}
          icon="ri-calendar-line"
          className="text-sm"
        />
      </div>
      <div>
        <Input
          type="date"
          placeholder={t('search.return')}
          icon="ri-calendar-line"
          className="text-sm"
        />
      </div>
    </div>
  );

  const renderActivitiesSearch = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="md:col-span-2">
        <Input
          placeholder={t('search.activity')}
          icon="ri-search-line"
          className="text-sm"
        />
      </div>
      <div>
        <Input
          type="date"
          placeholder={t('search.date')}
          icon="ri-calendar-line"
          className="text-sm"
        />
      </div>
    </div>
  );

  const renderHajjUmrahSearch = () => (
    <div className="space-y-6">
      {/* Package Type Selection */}
      <div className="space-y-4">
        <div className="flex space-x-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="hajjPackageType"
              value="hajj"
              checked={hajjPackageType === 'hajj'}
              onChange={(e) => setHajjPackageType(e.target.value)}
              className="mr-2"
            />
            {t('hajj.packages')}
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="hajjPackageType"
              value="umrah"
              checked={hajjPackageType === 'umrah'}
              onChange={(e) => setHajjPackageType(e.target.value)}
              className="mr-2"
            />
            {t('hajj.umrahPackages')}
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="hajjPackageType"
              value="custom"
              checked={hajjPackageType === 'custom'}
              onChange={(e) => setHajjPackageType(e.target.value)}
              className="mr-2"
            />
            Custom Package
          </label>
        </div>
      </div>

      {/* Basic Search Fields */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Input
            placeholder="Departure city"
            icon="ri-takeoff-line"
            className="text-sm"
          />
        </div>
        <div>
          <Input
            type="date"
            placeholder={t('search.departureDate')}
            icon="ri-calendar-line"
            className="text-sm"
          />
        </div>
        <div>
          <Input
            type="date"
            placeholder={t('search.returnDate')}
            icon="ri-calendar-line"
            className="text-sm"
          />
        </div>
      </div>

      {/* Custom Package Design Options */}
      {hajjPackageType === 'custom' && (
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <h3 className="font-semibold text-lg mb-4">Customize Your Package</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Accommodation Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Accommodation Level
              </label>
              <select 
                value={customPackage.accommodation}
                onChange={(e) => setCustomPackage(prev => ({ ...prev, accommodation: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
              >
                <option value="economy">Economy (3-star)</option>
                <option value="standard">Standard (4-star)</option>
                <option value="deluxe">Deluxe (5-star)</option>
                <option value="luxury">Luxury (5-star Premium)</option>
              </select>
            </div>

            {/* Transportation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transportation
              </label>
              <select 
                value={customPackage.transportation}
                onChange={(e) => setCustomPackage(prev => ({ ...prev, transportation: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
              >
                <option value="bus">Shared Bus</option>
                <option value="private-bus">Private Bus</option>
                <option value="car">Private Car</option>
                <option value="vip-transport">VIP Transport</option>
              </select>
            </div>

            {/* Meal Plan */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meal Plan
              </label>
              <select 
                value={customPackage.meals}
                onChange={(e) => setCustomPackage(prev => ({ ...prev, meals: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
              >
                <option value="no-meals">No Meals</option>
                <option value="breakfast">Breakfast Only</option>
                <option value="half-board">Half Board (Breakfast + Dinner)</option>
                <option value="full-board">Full Board (All Meals)</option>
              </select>
            </div>

            {/* Guidance Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Guidance & Support
              </label>
              <select 
                value={customPackage.guidance}
                onChange={(e) => setCustomPackage(prev => ({ ...prev, guidance: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
              >
                <option value="self-guided">Self-Guided</option>
                <option value="group">Group Guide</option>
                <option value="private">Private Guide</option>
                <option value="scholar">Religious Scholar Guide</option>
              </select>
            </div>

            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package Duration (Days)
              </label>
              <select 
                value={customPackage.duration}
                onChange={(e) => setCustomPackage(prev => ({ ...prev, duration: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-8"
              >
                <option value="7">7 Days</option>
                <option value="10">10 Days</option>
                <option value="14">14 Days</option>
                <option value="21">21 Days</option>
                <option value="30">30 Days</option>
                <option value="custom">Custom Duration</option>
              </select>
            </div>
          </div>

          {/* Additional Services */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Services
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" />
                Visa Processing
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" />
                Travel Insurance
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" />
                Airport Transfers
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" />
                Ziyarat Tours
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" />
                Laundry Service
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" />
                Medical Support
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" />
                Shopping Tours
              </label>
              <label className="flex items-center text-sm">
                <input type="checkbox" className="mr-2" />
                24/7 Support
              </label>
            </div>
          </div>

          {/* Special Requirements */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Special Requirements
            </label>
            <textarea
              placeholder="Any special dietary requirements, accessibility needs, or other preferences..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              rows={3}
              maxLength={500}
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderSearchForm = () => {
    switch (activeTab) {
      case 'hotels':
        return renderHotelsSearch();
      case 'flights':
        return renderFlightsSearch();
      case 'cars':
        return renderCarsSearch();
      case 'packages':
        return renderPackagesSearch();
      case 'activities':
        return renderActivitiesSearch();
      case 'hajj-umrah':
        return renderHajjUmrahSearch();
      default:
        return renderHotelsSearch();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 -mt-8 relative z-10 mx-4 max-w-6xl mx-auto">
      {/* Tabs */}
      <div className="flex flex-wrap border-b border-gray-200 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as SearchType)}
            className={`flex items-center space-x-2 px-4 py-3 border-b-2 font-medium text-sm cursor-pointer whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className={`${tab.icon} text-lg`}></i>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Search Form */}
      <div className="space-y-4">
        {renderSearchForm()}
        
        {/* Travelers/Rooms selector for applicable tabs */}
        {(activeTab === 'hotels' || activeTab === 'flights' || activeTab === 'packages' || activeTab === 'hajj-umrah') && (
          <div className="relative">
            <button
              onClick={() => setShowTravelers(!showTravelers)}
              className="flex items-center space-x-2 px-3 py-2.5 border border-gray-300 rounded-lg text-sm cursor-pointer"
            >
              <i className="ri-user-line text-gray-400"></i>
              <span>
                {travelers.adults} {travelers.adults !== 1 ? t('hotels.adults') : t('hotels.adults')}
                {travelers.children > 0 && `, ${travelers.children} ${t('hotels.children')}`}
                {activeTab === 'hotels' && `, ${travelers.rooms} ${t('hotels.rooms')}`}
              </span>
              <i className="ri-arrow-down-s-line text-gray-400"></i>
            </button>
            
            {showTravelers && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-20 w-64">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t('hotels.adults')}</span>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setTravelers(prev => ({ ...prev, adults: Math.max(1, prev.adults - 1) }))}
                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center cursor-pointer"
                      >
                        <i className="ri-subtract-line text-sm"></i>
                      </button>
                      <span className="w-8 text-center">{travelers.adults}</span>
                      <button
                        onClick={() => setTravelers(prev => ({ ...prev, adults: prev.adults + 1 }))}
                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center cursor-pointer"
                      >
                        <i className="ri-add-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t('hotels.children')}</span>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setTravelers(prev => ({ ...prev, children: Math.max(0, prev.children - 1) }))}
                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center cursor-pointer"
                      >
                        <i className="ri-subtract-line text-sm"></i>
                      </button>
                      <span className="w-8 text-center">{travelers.children}</span>
                      <button
                        onClick={() => setTravelers(prev => ({ ...prev, children: prev.children + 1 }))}
                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center cursor-pointer"
                      >
                        <i className="ri-add-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                  
                  {activeTab === 'hotels' && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{t('hotels.rooms')}</span>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => setTravelers(prev => ({ ...prev, rooms: Math.max(1, prev.rooms - 1) }))}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center cursor-pointer"
                        >
                          <i className="ri-subtract-line text-sm"></i>
                        </button>
                        <span className="w-8 text-center">{travelers.rooms}</span>
                        <button
                          onClick={() => setTravelers(prev => ({ ...prev, rooms: prev.rooms + 1 }))}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center cursor-pointer"
                        >
                          <i className="ri-add-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <Button
                    size="sm"
                    fullWidth
                    onClick={() => setShowTravelers(false)}
                  >
                    Done
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Search Button */}
        <div className="flex justify-end">
          <Button size="lg" className="px-8" onClick={handleSearch}>
            <i className="ri-search-line mr-2"></i>
            {t('search.searchButton')}
          </Button>
        </div>
      </div>
    </div>
  );
}